import React, { useMemo, useState } from "react";
import {
	AlertCircle,
	Check,
	CheckCircle2,
	Copy,
	Eye,
	EyeOff,
	Wand2,
} from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { cn } from "../../lib/utils";

// ─────────────────────────────────────────────────────────────────────────────
// Espelho da regra do backend (backend/src/helpers/ValidarSenha.ts).
//
// A regra nasceu dentro do reset de senha e era copiada à mão aqui no front.
// Agora existe uma fonte só no backend e este arquivo é o espelho dela: serve
// para dar retorno na hora que a pessoa digita, mas quem decide de verdade
// continua sendo o backend (ERR_WEAK_PASSWORD).
//
// Ao mexer em um lado, mexa no outro.
// ─────────────────────────────────────────────────────────────────────────────

export const REGRA_SENHA = Object.freeze({
	minLength: 8,
	maxLength: 16,
	regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+{};:,<.>/?\[\]\\|`~"'º°ª¢£¬§´¨¹²³£¢¬]).{8,16}$/,
	descricao:
		"A senha deve ter de 8 a 16 caracteres e conter: pelo menos 1 letra maiúscula, 1 letra minúscula, 1 número e 1 caractere especial (ex.: ! @ # $ % & * - _ + =).",
});

const REGEX_ESPECIAL = /[!@#$%^&*()\-_=+{};:,<.>/?\[\]\\|`~"'º°ª¢£¬§´¨¹²³]/;

// As primeiras que qualquer um tenta (mesma lista do backend).
const SENHAS_PROIBIDAS = new Set([
	"password",
	"password1",
	"passw0rd",
	"senha",
	"senha123",
	"senha@123",
	"secret",
	"123456",
	"1234567",
	"12345678",
	"123456789",
	"1234567890",
	"qwerty",
	"qwerty123",
	"qwertyuiop",
	"asdf1234",
	"abc123",
	"abcd1234",
	"a1b2c3",
	"1q2w3e4r",
	"zaq12wsx",
	"admin",
	"admin123",
	"administrador",
	"root",
	"toor",
	"letmein",
	"welcome",
	"iloveyou",
	"monkey",
	"dragon",
	"master",
	"mudar123",
	"trocar123",
	"trocar@123",
	"perabit",
	"perabit123",
	"pera123",
]);

const normalizar = (valor) =>
	String(valor || "")
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.toLowerCase();

// "abcd", "1234", "4321": quatro ou mais caracteres vizinhos.
const temSequencia = (senha) => {
	let crescente = 1;
	let decrescente = 1;
	for (let i = 1; i < senha.length; i += 1) {
		const atual = senha.charCodeAt(i);
		const anterior = senha.charCodeAt(i - 1);
		crescente = atual - anterior === 1 ? crescente + 1 : 1;
		decrescente = anterior - atual === 1 ? decrescente + 1 : 1;
		if (crescente >= 4 || decrescente >= 4) return true;
	}
	return false;
};

// "aaaa", "1111": repetir não aumenta a força, só o comprimento.
const temRepeticao = (senha) => {
	let iguais = 1;
	for (let i = 1; i < senha.length; i += 1) {
		iguais = senha[i] === senha[i - 1] ? iguais + 1 : 1;
		if (iguais >= 4) return true;
	}
	return false;
};

// Nome e e-mail da própria pessoa são as primeiras tentativas de quem quer
// entrar na conta dela.
const contemDadosPessoais = (senha, nome, email) => {
	const alvo = normalizar(senha);
	const pedacos = [];

	const nomeNormalizado = normalizar(nome).trim();
	if (nomeNormalizado.length >= 3) {
		pedacos.push(nomeNormalizado);
		nomeNormalizado.split(/\s+/).forEach((parte) => {
			if (parte.length >= 3) pedacos.push(parte);
		});
	}

	const emailNormalizado = normalizar(email).trim();
	if (emailNormalizado.includes("@")) {
		const local = emailNormalizado.split("@")[0];
		if (local.length >= 3) pedacos.push(local);
	}

	return pedacos.some((pedaco) => alvo.includes(pedaco));
};

// Mesmo retorno do backend, mais o que a tela precisa (pct/label/regras).
// `motivo` diz qual checagem barrou — usado para não mostrar "tudo verde" no
// checklist quando o bloqueio veio de uma regra extra (senha comum etc.).
export const avaliarSenha = (valor, contexto = {}) => {
	const senha = String(valor ?? "");
	const { nome, email } = contexto;

	const len =
		senha.length >= REGRA_SENHA.minLength &&
		senha.length <= REGRA_SENHA.maxLength;
	const lower = /[a-z]/.test(senha);
	const upper = /[A-Z]/.test(senha);
	const num = /\d/.test(senha);
	const special = REGEX_ESPECIAL.test(senha);
	const composicao = len && lower && upper && num && special;

	const score = [lower, upper, num, special].filter(Boolean).length;
	const pontos = score + (len ? 1 : 0);

	const base = {
		score,
		pct: Math.round((pontos / 5) * 100),
		regras: { len, lower, upper, num, special },
	};

	if (!senha) {
		return {
			...base,
			ok: false,
			motivo: "vazia",
			erro: "Digite uma senha.",
			forca: "fraca",
			pct: 0,
		};
	}

	if (senha.length < REGRA_SENHA.minLength) {
		return {
			...base,
			ok: false,
			motivo: "curta",
			erro: `A senha deve ter no mínimo ${REGRA_SENHA.minLength} caracteres.`,
			forca: "fraca",
		};
	}

	if (senha.length > REGRA_SENHA.maxLength) {
		return {
			...base,
			ok: false,
			motivo: "longa",
			erro: `A senha deve ter no máximo ${REGRA_SENHA.maxLength} caracteres.`,
			forca: "fraca",
			pct: 100,
		};
	}

	if (!composicao) {
		// Frase curta listando só o que falta. A descrição completa
		// (REGRA_SENHA.descricao) tem 3 linhas e estourava o espaço
		// reservado para o aviso — e o checklist acima já explica tudo.
		const faltando = [];
		if (!lower) faltando.push("1 minúscula");
		if (!upper) faltando.push("1 maiúscula");
		if (!num) faltando.push("1 número");
		if (!special) faltando.push("1 símbolo");
		return {
			...base,
			ok: false,
			motivo: "composicao",
			erro: faltando.length
				? `Falta: ${faltando.join(", ")}.`
				: "A senha não atende aos requisitos.",
			forca: "fraca",
		};
	}

	// Composição ok. Daqui pra baixo é o que "fica composto, mas é adivinhável".
	if (SENHAS_PROIBIDAS.has(normalizar(senha))) {
		return {
			...base,
			ok: false,
			motivo: "comum",
			erro: "Essa senha é muito comum. Escolha outra.",
			forca: "fraca",
		};
	}

	if (temSequencia(senha)) {
		return {
			...base,
			ok: false,
			motivo: "sequencia",
			erro: "Evite sequências como 1234 ou abcd. Escolha uma senha sem letras ou números em ordem.",
			forca: "fraca",
		};
	}

	if (temRepeticao(senha)) {
		return {
			...base,
			ok: false,
			motivo: "repeticao",
			erro: "Evite repetir o mesmo caractere quatro vezes seguidas. Escolha outra senha.",
			forca: "fraca",
		};
	}

	if (contemDadosPessoais(senha, nome, email)) {
		return {
			...base,
			ok: false,
			motivo: "pessoal",
			erro: "A senha não pode conter o seu nome ou o seu e-mail.",
			forca: "fraca",
		};
	}

	return {
		...base,
		ok: true,
		motivo: null,
		erro: "",
		forca: senha.length >= 12 ? "segura" : "moderada",
	};
};

// Alfabetos sem caracteres que se confundem (l/I, O/0).
const MINUSCULAS = "abcdefghijkmnpqrstuvwxyz";
const MAIUSCULAS = "ABCDEFGHJKLMNPQRSTUVWXYZ";
const NUMEROS = "23456789";
const ESPECIAIS = "!@#$%&*-_+=";

const sortearIndice = (limite) => {
	const buffer = new Uint8Array(1);
	let valor;
	do {
		crypto.getRandomValues(buffer);
		valor = buffer[0];
	} while (valor >= 256 - (256 % limite));
	return valor % limite;
};

const sortearCaractere = (alfabeto) =>
	alfabeto[sortearIndice(alfabeto.length)];

// Senha pronta de 16 caracteres, 1 de cada classe, embaralhada e conferida
// pela mesma regra. É o botão "Gerar senha forte" dos modais.
export const gerarSenhaForte = (comprimento = 16) => {
	const total = Math.max(
		REGRA_SENHA.minLength,
		Math.min(comprimento, REGRA_SENHA.maxLength)
	);
	const todos = MINUSCULAS + MAIUSCULAS + NUMEROS + ESPECIAIS;

	for (let tentativa = 0; tentativa < 30; tentativa += 1) {
		const caracteres = [
			sortearCaractere(MINUSCULAS),
			sortearCaractere(MAIUSCULAS),
			sortearCaractere(NUMEROS),
			sortearCaractere(ESPECIAIS),
		];
		while (caracteres.length < total) {
			caracteres.push(sortearCaractere(todos));
		}
		for (let i = caracteres.length - 1; i > 0; i -= 1) {
			const j = sortearIndice(i + 1);
			[caracteres[i], caracteres[j]] = [caracteres[j], caracteres[i]];
		}
		const senha = caracteres.join("");
		if (avaliarSenha(senha).ok) return senha;
	}

	// Só cai aqui se o sorteio falhar 30 vezes seguidas (praticamente impossível).
	return "Perabit#K7vQ2mXz";
};

const ITENS_CHECKLIST = [
	{ chave: "len", texto: "8 a 16 caracteres" },
	{ chave: "lower", texto: "1 letra minúscula" },
	{ chave: "upper", texto: "1 letra maiúscula" },
	{ chave: "num", texto: "1 número" },
	{ chave: "special", texto: "1 caractere especial" },
];

const CORES_POR_FORCA = {
	fraca: { barra: "bg-red-500", texto: "text-red-500" },
	moderada: { barra: "bg-amber-500", texto: "text-amber-500" },
	segura: { barra: "bg-emerald-500", texto: "text-emerald-500" },
};

const CORES_MENSAGEM = {
	dica: "text-muted-foreground",
	erro: "text-destructive",
	atencao: "text-amber-600",
	ok: "text-emerald-600",
};

const SenhaForte = ({
	id = "senha",
	label = "Senha",
	value = "",
	onChange,
	onBlur,
	placeholder,
	nome,
	email,
	error,
	touched,
	disabled = false,
	autoFocus = false,
	autoComplete = "new-password",
	mostrarForca = true,
	mostrarChecklist = true,
	mostrarGerador = true,
	onGerar,
	className,
}) => {
	const [visivel, setVisivel] = useState(false);
	const [copiado, setCopiado] = useState(false);

	const avaliacao = useMemo(
		() => avaliarSenha(value, { nome, email }),
		[value, nome, email]
	);

	const cores = CORES_POR_FORCA[avaliacao.forca] || CORES_POR_FORCA.fraca;
	const temValor = String(value || "").length > 0;

	// Composição toda verde, mas barrada por uma regra extra (senha comum,
	// sequência, repetição ou dados pessoais): o aviso fica âmbar em vez de
	// vermelho, porque o que falta não é "formato", é escolher outra senha.
	const composicaoOk =
		avaliacao.regras.len &&
		avaliacao.regras.lower &&
		avaliacao.regras.upper &&
		avaliacao.regras.num &&
		avaliacao.regras.special;

	// Uma linha de retorno só, e SEMPRE presente: aparecendo e desaparecendo
	// ela empurrava o resto do formulário a cada tecla. O erro do Yup (quando
	// existe) tem prioridade, senão vale o retorno ao vivo.
	const mensagem = useMemo(() => {
		const doYup = touched && error ? String(error) : "";
		if (doYup) {
			return { texto: doYup, tom: composicaoOk ? "atencao" : "erro" };
		}
		if (!temValor) {
			return {
				texto:
					"De 8 a 16 caracteres, com maiúscula, minúscula, número e símbolo.",
				tom: "dica",
			};
		}
		if (!avaliacao.ok) {
			return { texto: avaliacao.erro, tom: composicaoOk ? "atencao" : "erro" };
		}
		return {
			texto: avaliacao.forca === "segura" ? "Senha forte." : "Senha aceita.",
			tom: "ok",
		};
	}, [touched, error, temValor, composicaoOk, avaliacao.ok, avaliacao.erro, avaliacao.forca]);

	const gerar = () => {
		const nova = gerarSenhaForte();
		setVisivel(true);
		setCopiado(false);
		if (onChange) onChange(nova);
		// Quem usa o gerador costuma querer obrigar a troca no próximo login —
		// o modal decide o que fazer com isso.
		if (onGerar) onGerar(nova);
	};

	const copiar = async () => {
		const texto = String(value || "");
		if (!texto) return;
		try {
			if (navigator.clipboard && window.isSecureContext) {
				await navigator.clipboard.writeText(texto);
			} else {
				// http na rede local: clipboard API não existe, cai no legado.
				const area = document.createElement("textarea");
				area.value = texto;
				area.style.position = "fixed";
				area.style.opacity = "0";
				document.body.appendChild(area);
				area.select();
				document.execCommand("copy");
				document.body.removeChild(area);
			}
			setCopiado(true);
			setTimeout(() => setCopiado(false), 1600);
		} catch (e) {
			// clipboard bloqueado pelo navegador: a senha segue visível na tela.
		}
	};

	return (
		<div className={cn("space-y-1.5", className)}>
			{label && <Label htmlFor={id}>{label}</Label>}

			<div className="relative">
				<Input
					id={id}
					name={id}
					value={value}
					onChange={(e) => onChange && onChange(e.target.value)}
					onBlur={onBlur}
					type={visivel ? "text" : "password"}
					className="pr-9"
					placeholder={placeholder}
					disabled={disabled}
					autoFocus={autoFocus}
					autoComplete={autoComplete}
					spellCheck={false}
					// O teto também trava a digitação: sem isso a pessoa só
					// descobriria o limite depois de tomar erro no formulário.
					maxLength={REGRA_SENHA.maxLength}
				/>
				<button
					type="button"
					tabIndex={-1}
					onClick={() => setVisivel((v) => !v)}
					aria-label={visivel ? "Ocultar senha" : "Mostrar senha"}
					className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
				>
					{visivel ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
				</button>
			</div>

			{/* Tudo abaixo fica SEMPRE no lugar: o bloco era renderizado só
			    quando havia senha, então o modal inteiro pulava na primeira
			    tecla. Agora o que muda é só a cor e o preenchimento. */}
			{mostrarGerador && (
				<div className="flex flex-wrap items-center gap-2">
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={gerar}
						disabled={disabled}
					>
						<Wand2 className="h-3.5 w-3.5" />
						Gerar senha forte
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="sm"
						onClick={copiar}
						disabled={disabled || !temValor}
					>
						{copiado ? (
							<Check className="h-3.5 w-3.5 text-emerald-500" />
						) : (
							<Copy className="h-3.5 w-3.5" />
						)}
						{copiado ? "Copiada" : "Copiar"}
					</Button>
				</div>
			)}

			{mostrarForca && (
				<div className="space-y-1">
					<div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
						<div
							className={cn(
								"h-full rounded-full transition-all",
								temValor ? cores.barra : "bg-transparent"
							)}
							style={{ width: `${temValor ? avaliacao.pct : 0}%` }}
						/>
					</div>
					<div className="flex items-center justify-between text-[11px]">
						<span
							className={cn(
								"font-medium",
								temValor && "capitalize",
								temValor ? cores.texto : "text-muted-foreground"
							)}
						>
							{temValor ? avaliacao.forca : "Força da senha"}
						</span>
						<span className="text-muted-foreground">
							{String(value).length}/{REGRA_SENHA.maxLength}
						</span>
					</div>
				</div>
			)}

			{mostrarChecklist && (
				<ul className="flex flex-wrap gap-x-3 gap-y-1">
					{ITENS_CHECKLIST.map((item) => {
						const atendida = avaliacao.regras[item.chave];
						return (
							<li
								key={item.chave}
								className={cn(
									"flex items-center gap-1.5 text-[11px]",
									atendida ? "text-emerald-600" : "text-muted-foreground"
								)}
							>
								{atendida ? (
									<CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
								) : (
									<AlertCircle className="h-3.5 w-3.5 shrink-0" />
								)}
								{item.texto}
							</li>
						);
					})}
				</ul>
			)}

			{/* Duas linhas reservadas (leading-4 = 16px cada): a altura não muda
			    entre dica, erro e senha aprovada. */}
			<p
				className={cn(
					"min-h-8 text-xs leading-4",
					CORES_MENSAGEM[mensagem.tom]
				)}
			>
				{mensagem.texto}
			</p>
		</div>
	);
};

export default SenhaForte;
